import axios from "axios";

export const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:5220/api";

class HttpClient {
  static _instance = null;

  constructor() {
    if (HttpClient._instance) return HttpClient._instance;

    this.client = axios.create({
      baseURL: API_URL,
      timeout: 600000,
    });

    this.isRefreshing = false;
    this.failedQueue = [];

    this.client.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry) {
          if (this.isRefreshing) {
            // Jeśli już odświeżamy token, dodaj request do kolejki
            return new Promise((resolve, reject) => {
              this.failedQueue.push({ resolve, reject });
            })
              .then((token) => {
                originalRequest.headers["Authorization"] = `Bearer ${token}`;
                return this.client(originalRequest);
              })
              .catch((err) => {
                return Promise.reject(err);
              });
          }

          originalRequest._retry = true;
          this.isRefreshing = true;

          try {
            // Spróbuj odświeżyć token przez Keycloak
            const keycloak = window.keycloakInstance;
            if (keycloak && keycloak.refreshToken) {
              const refreshed = await keycloak.updateToken(30);
              if (refreshed && keycloak.token) {
                localStorage.setItem("token", keycloak.token);

                // Przetwórz kolejkę oczekujących requestów
                this.processQueue(null, keycloak.token);

                // Ponów pierwotny request
                originalRequest.headers[
                  "Authorization"
                ] = `Bearer ${keycloak.token}`;
                return this.client(originalRequest);
              }
            }

            // Jeśli odświeżanie nie powiodło się
            this.processQueue(new Error("Token refresh failed"), null);
            this.logout();
            return Promise.reject(error);
          } catch (refreshError) {
            this.processQueue(refreshError, null);
            this.logout();
            return Promise.reject(refreshError);
          } finally {
            this.isRefreshing = false;
          }
        }

        return Promise.reject(error);
      }
    );

    HttpClient._instance = this;
  }

  processQueue(error, token = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  logout() {
    localStorage.removeItem("token");
    localStorage.removeItem("hasLoggedIn");
    if (window.keycloakInstance) {
      window.keycloakInstance.logout();
    } else {
      window.location.href = "/";
    }
  }

  _attachAuthHeaders(config = {}) {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers = {
        ...(config.headers || {}),
        Authorization: `Bearer ${token}`,
      };
    }
    return config;
  }

  async get(url, { params = {}, withAuth = false, config = {} } = {}) {
    const finalConfig = {
      ...config,
      params,
      ...(withAuth ? this._attachAuthHeaders(config) : {}),
    };
    return this.client.get(url, finalConfig);
  }

  async post(url, data = {}, { withAuth = false, config = {} } = {}) {
    const finalConfig = {
      ...(withAuth ? this._attachAuthHeaders(config) : {}),
      ...config,
    };
    return this.client.post(url, data, finalConfig);
  }

  async put(url, data = {}, { withAuth = false, config = {} } = {}) {
    const finalConfig = {
      ...(withAuth ? this._attachAuthHeaders(config) : {}),
      ...config,
    };
    return this.client.put(url, data, finalConfig);
  }

  async delete(url, { withAuth = false, config = {} } = {}) {
    const finalConfig = withAuth ? this._attachAuthHeaders(config) : config;
    return this.client.delete(url, finalConfig);
  }
}

const httpClient = new HttpClient();
export default httpClient;
